import { type Box, distance, inBox, stepToward } from './geometry';
import type { Rng } from './rng';
import { PERIMETER_Z, RUNOUT } from './spawn';
import type { Agent, KioskState, Point, World } from './types';

/** Seconds spent watching the screen. */
const WATCH_TIME = { min: 8, max: 20 } as const;
/** Seconds spent on a kiosk demo. */
const DEMO_TIME = { min: 25, max: 60 } as const;
/** Pace while glancing in from the aisle. */
const GLANCE_SPEED = 1.8;
/** Spacing between people in a kiosk queue. */
const QUEUE_SPACING = 2.2;
/** Longest queue anyone will join before giving up and walking on. */
export const MAX_QUEUE = 4;

/** The point on the aisle lane this agent is walking along, at a given progress. */
function lanePoint(a: Agent, world: World, along: number): Point {
  return a.lane === 'main'
    ? { x: along, z: world.depth + a.laneOffset }
    : { x: world.width + a.laneOffset, z: along };
}

function alongOf(a: Agent): number {
  return a.lane === 'main' ? a.x : a.z;
}

/** Where this agent leaves the model. */
function exitPoint(a: Agent, world: World): Point {
  const far =
    a.lane === 'main'
      ? world.width + RUNOUT
      : world.depth + world.aisle + RUNOUT;
  const near = a.lane === 'main' ? -world.aisle - RUNOUT : PERIMETER_Z;
  return lanePoint(a, world, a.dir === 1 ? far : near);
}

/** True once the agent is level with the booth's open edge on its lane. */
function besideBooth(a: Agent, world: World): boolean {
  const along = alongOf(a);
  const span = a.lane === 'main' ? [0, world.width] : [0, world.depth];
  return along >= span[0]! && along <= span[1]!;
}

function pastBooth(a: Agent, world: World): boolean {
  const along = alongOf(a);
  const end = a.lane === 'main' ? world.width : world.depth;
  return a.dir === 1 ? along > end : along < 0;
}

function queuePoint(k: KioskState, index: number): Point {
  // Queue forms away from the back wall, toward the aisle.
  return { x: k.approach.x, z: k.approach.z + QUEUE_SPACING * (index + 1) };
}

/**
 * How far someone will walk to skip one person ahead of them. A demo runs
 * 25–60 s and people stroll at ~4 ft/s, so one person ahead is worth
 * roughly a booth-width of walking.
 */
export const FEET_PER_PERSON_AHEAD = 16;

/** People already at or waiting for a station. */
function waitingAt(k: KioskState): number {
  return k.queue.length + (k.occupant === null ? 0 : 1);
}

/**
 * The station this attendee heads for: the one with the least people ahead,
 * with distance breaking near-ties, so a free laptop across the booth beats
 * a line at the near one. Full queues are never chosen.
 */
export function chooseStation(
  a: Agent,
  kiosks: KioskState[],
): KioskState | undefined {
  let best: KioskState | undefined;
  let bestCost = Infinity;
  for (const k of kiosks) {
    if (k.queue.length >= MAX_QUEUE) continue;
    const cost = distance(a, k.approach) + waitingAt(k) * FEET_PER_PERSON_AHEAD;
    if (cost < bestCost) {
      bestCost = cost;
      best = k;
    }
  }
  return best;
}

function face(a: Agent, to: Point): void {
  a.heading = Math.atan2(to.x - a.x, to.z - a.z);
}

function walk(
  a: Agent,
  to: Point,
  dt: number,
  obstacles: Box[],
  speed = a.speed,
): boolean {
  const { p, arrived } = stepToward(a, to, speed * dt, obstacles);
  const moved = p.x !== a.x || p.z !== a.z;
  if (moved) face(a, p);
  a.stuck = moved || arrived ? 0 : a.stuck + dt;
  a.x = p.x;
  a.z = p.z;
  return arrived;
}

/** Seconds of no progress before an attendee gives up on wherever they were going. */
const GIVE_UP_AFTER = 4;

/** A spot to watch from that is not inside any furniture; null if none found. */
function pickWatchSpot(world: World, obstacles: Box[], rng: Rng): Point | null {
  for (let i = 0; i < 8; i++) {
    const p = {
      x:
        world.watchSpot.x +
        rng.range(-world.watchSpot.spread, world.watchSpot.spread),
      z: world.watchSpot.z + rng.range(-0.8, 0.8),
    };
    if (!obstacles.some((b) => inBox(p, b))) return p;
  }
  return null;
}

/** Marks the agent as finished with the booth and sends it back down its lane. */
function leave(a: Agent, world: World): void {
  a.state = 'leaving';
  a.target = exitPoint(a, world);
  a.kioskId = null;
}

export interface StepContext {
  world: World;
  kiosks: KioskState[];
  obstacles: Box[];
  rng: Rng;
  dt: number;
}

/**
 * Advances one attendee. Walking agents follow their lane until they are
 * beside the booth, then act on their intent. Everything comes back to the
 * lane and exits the same way it came, further along.
 */
export function stepAgent(a: Agent, ctx: StepContext): void {
  const { world, kiosks, obstacles, rng, dt } = ctx;
  if (a.stuck > GIVE_UP_AFTER) {
    a.stuck = 0;
    if (a.state === 'leaving') {
      a.state = 'done';
      return;
    }
    const kiosk = kiosks.find((k) => k.id === a.kioskId);
    if (kiosk) {
      kiosk.queue = kiosk.queue.filter((id) => id !== a.id);
      if (kiosk.occupant === a.id) kiosk.occupant = null;
    }
    leave(a, world);
    return;
  }
  switch (a.state) {
    case 'walking': {
      if (
        a.intent !== 'pass' &&
        !pastBooth(a, world) &&
        besideBooth(a, world)
      ) {
        if (a.intent === 'glance') {
          walk(a, exitPoint(a, world), dt, obstacles, GLANCE_SPEED);
          face(a, { x: world.width / 2, z: world.depth / 2 });
          if (pastBooth(a, world)) a.intent = 'pass';
          return;
        }
        if (a.intent === 'watch') {
          const spot = pickWatchSpot(world, obstacles, rng);
          if (!spot) {
            a.intent = 'glance';
            return;
          }
          a.target = spot;
          a.state = 'watching';
          a.timer = -1;
          return;
        }
        const kiosk = chooseStation(a, kiosks);
        if (!kiosk) {
          a.intent = 'glance';
          return;
        }
        a.kioskId = kiosk.id;
        kiosk.queue.push(a.id);
        kiosk.maxQueue = Math.max(kiosk.maxQueue, kiosk.queue.length);
        a.state = 'queued';
        return;
      }
      if (walk(a, exitPoint(a, world), dt, obstacles)) a.state = 'done';
      return;
    }
    case 'watching': {
      if (a.timer < 0) {
        if (walk(a, a.target!, dt, obstacles)) {
          a.timer = rng.range(WATCH_TIME.min, WATCH_TIME.max);
          face(a, { x: world.watchSpot.x, z: 0 });
        }
        return;
      }
      a.timer -= dt;
      if (a.timer <= 0) leave(a, world);
      return;
    }
    case 'queued': {
      const kiosk = kiosks.find((k) => k.id === a.kioskId);
      if (!kiosk) return leave(a, world);
      const index = kiosk.queue.indexOf(a.id);
      if (index === 0 && kiosk.occupant === null) {
        kiosk.queue.shift();
        kiosk.occupant = a.id;
        a.state = 'demo';
        a.timer = -1;
        return;
      }
      walk(a, queuePoint(kiosk, index), dt, obstacles);
      face(a, kiosk.approach);
      return;
    }
    case 'demo': {
      const kiosk = kiosks.find((k) => k.id === a.kioskId);
      if (!kiosk) return leave(a, world);
      if (a.timer < 0) {
        if (walk(a, kiosk.approach, dt, obstacles)) {
          a.timer = rng.range(DEMO_TIME.min, DEMO_TIME.max);
          face(a, { x: kiosk.approach.x, z: kiosk.approach.z - 1 });
        }
        return;
      }
      a.timer -= dt;
      if (a.timer <= 0) {
        kiosk.occupant = null;
        kiosk.demosDone += 1;
        leave(a, world);
      }
      return;
    }
    case 'leaving': {
      if (walk(a, a.target!, dt, obstacles)) a.state = 'done';
      return;
    }
    case 'done':
      return;
  }
}

/** Nudges people apart so a crowd reads as a crowd, not a stack. */
export function separate(agents: Agent[], minGap = 1.4): void {
  for (let i = 0; i < agents.length; i++) {
    const a = agents[i]!;
    if (a.state === 'done') continue;
    for (let j = i + 1; j < agents.length; j++) {
      const b = agents[j]!;
      if (b.state === 'done') continue;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const d = Math.hypot(dx, dz);
      if (d === 0 || d >= minGap) continue;
      const push = ((minGap - d) / 2) * 0.5;
      const ux = dx / d;
      const uz = dz / d;
      a.x -= ux * push;
      a.z -= uz * push;
      b.x += ux * push;
      b.z += uz * push;
    }
  }
}
